"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePdfViewerContext } from "./PdfViewerCore";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";

type PdfSearchBarProps = {
  open: boolean;
  onClose: () => void;
};

export const PdfSearchBar = memo(function PdfSearchBar({
  open,
  onClose,
}: PdfSearchBarProps) {
  const { t } = useTranslation();
  const { instanceRef, isReady } = usePdfViewerContext();
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);

  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [matchCurrent, setMatchCurrent] = useState(0);
  const [matchTotal, setMatchTotal] = useState(0);
  const [notFound, setNotFound] = useState(false);

  // Track whether the initial search has been dispatched for the current query
  // so subsequent Enter presses use "again" instead of re-dispatching.
  const hasSearchedRef = useRef(false);

  // Auto-focus input when opened.
  useEffect(() => {
    if (open) {
      // Small delay to ensure the element is mounted and painted.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Listen to pdf.js find events.
  useEffect(() => {
    if (!isReady || !open) return;
    const eb = instanceRef.current?.eventBus;
    if (!eb) return;

    const onMatchCount = (e: { matchesCount: { current: number; total: number } }) => {
      setMatchCurrent(e.matchesCount.current);
      setMatchTotal(e.matchesCount.total);
      setNotFound(e.matchesCount.total === 0);
    };

    const onFindState = (e: { state: number; matchesCount: { current: number; total: number } }) => {
      // state: 0=FOUND, 1=NOT_FOUND, 2=WRAPPED, 3=PENDING
      setNotFound(e.state === 1);
      setMatchCurrent(e.matchesCount.current);
      setMatchTotal(e.matchesCount.total);
    };

    eb.on("updatefindmatchescount", onMatchCount);
    eb.on("updatefindcontrolstate", onFindState);
    return () => {
      eb.off("updatefindmatchescount", onMatchCount);
      eb.off("updatefindcontrolstate", onFindState);
    };
  }, [isReady, open, instanceRef]);

  // Reset when active document changes.
  useEffect(() => {
    setMatchCurrent(0);
    setMatchTotal(0);
    setNotFound(false);
    hasSearchedRef.current = false;

    // Re-dispatch current query on the new document.
    if (open && query.length > 0 && isReady) {
      const eb = instanceRef.current?.eventBus;
      if (eb) {
        eb.dispatch("find", {
          source: window,
          type: "",
          query,
          caseSensitive: false,
          entireWord: false,
          highlightAll: true,
          findPrevious: false,
          matchDiacritics: false,
        });
        hasSearchedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocumentId]);

  const dispatchFind = useCallback(
    (findQuery: string, findPrevious = false) => {
      const eb = instanceRef.current?.eventBus;
      if (!eb || !findQuery) return;

      const isNewSearch = !hasSearchedRef.current;
      eb.dispatch("find", {
        source: window,
        type: isNewSearch ? "" : "again",
        query: findQuery,
        caseSensitive: false,
        entireWord: false,
        highlightAll: true,
        findPrevious,
        matchDiacritics: false,
      });
      hasSearchedRef.current = true;
    },
    [instanceRef],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      hasSearchedRef.current = false;

      if (newQuery.length > 0) {
        dispatchFind(newQuery);
      } else {
        // Clear results when input is emptied.
        setMatchCurrent(0);
        setMatchTotal(0);
        setNotFound(false);
        instanceRef.current?.eventBus.dispatch("findbarclose", { source: window });
      }
    },
    [dispatchFind, instanceRef],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        dispatchFind(query, e.shiftKey);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [query, dispatchFind, onClose],
  );

  const handleClose = useCallback(() => {
    instanceRef.current?.eventBus.dispatch("findbarclose", { source: window });
    setQuery("");
    setMatchCurrent(0);
    setMatchTotal(0);
    setNotFound(false);
    hasSearchedRef.current = false;
    onClose();
  }, [instanceRef, onClose]);

  if (!open) return null;

  return (
    <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-border/50 bg-muted/30 px-2 text-xs text-muted-foreground">
      <SearchIcon className="size-3.5 shrink-0" />

      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={t("pdf.searchPlaceholder")}
        className="h-6 flex-1 min-w-0 rounded border border-border/50 bg-transparent px-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />

      {/* Match count or "not found" */}
      {query.length > 0 && (
        <span
          className={`shrink-0 tabular-nums ${notFound ? "text-destructive" : ""}`}
        >
          {notFound ? t("pdf.notFound") : `${matchCurrent} / ${matchTotal}`}
        </span>
      )}

      <TooltipIconButton
        tooltip={t("pdf.findPrevious")}
        className="size-6 cursor-pointer"
        onClick={() => dispatchFind(query, true)}
        disabled={matchTotal === 0}
      >
        <ChevronUpIcon className="size-3.5" />
      </TooltipIconButton>

      <TooltipIconButton
        tooltip={t("pdf.findNext")}
        className="size-6 cursor-pointer"
        onClick={() => dispatchFind(query, false)}
        disabled={matchTotal === 0}
      >
        <ChevronDownIcon className="size-3.5" />
      </TooltipIconButton>

      <TooltipIconButton
        tooltip={t("pdf.closeSearch")}
        className="size-6 cursor-pointer"
        onClick={handleClose}
      >
        <XIcon className="size-3.5" />
      </TooltipIconButton>
    </div>
  );
});
